import { Box, Typography, useTheme, alpha } from "@mui/material";

// Modern flat arc gauge — a 270° track with a colored value arc and an end cap,
// value in the centre. Deliberately not eCalc's skeuomorphic needle-and-ticks
// dial: thin track, bold value arc, big number, semantic color.

const START = -135; // degrees, 0° at top, clockwise positive
const SWEEP = 270;

const polar = (cx, cy, r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

const arcPath = (cx, cy, r, a0, a1) => {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  const large = a1 - a0 <= 180 ? 0 : 1;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
};

/**
 * @param {number|null} value  displayed number (null → "—", empty gauge)
 * @param {number} fraction    0..1 fill along the arc
 * @param {string} color       value-arc color (semantic)
 * @param {string} unit        small suffix inside the dial
 * @param {string} label       caption under the dial
 */
const GaugeDial = ({ value, fraction = 0, color, unit, label }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const track = alpha(theme.palette.text.primary, isDark ? 0.12 : 0.1);
  const c = color || theme.palette.text.secondary;

  const f = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0));
  const cx = 50;
  const cy = 50;
  const r = 39;
  const valEnd = START + f * SWEEP;
  const [capX, capY] = polar(cx, cy, r, valEnd);
  const hasValue = value !== null && value !== undefined;

  return (
    <Box sx={{ flex: "1 1 150px", minWidth: 140, maxWidth: 200, textAlign: "center" }}>
      <Box sx={{ position: "relative", width: "100%", maxWidth: 168, mx: "auto" }}>
        <svg viewBox="0 0 100 100" style={{ width: "100%", display: "block" }}>
          <path d={arcPath(cx, cy, r, START, START + SWEEP)} fill="none" stroke={track} strokeWidth={9} strokeLinecap="round" />
          {hasValue && f > 0 && (
            <path d={arcPath(cx, cy, r, START, valEnd)} fill="none" stroke={c} strokeWidth={9} strokeLinecap="round" />
          )}
          {hasValue && f > 0 && <circle cx={capX} cy={capY} r={5.5} fill={c} />}
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1, color: hasValue ? theme.palette.text.primary : theme.palette.text.disabled }}>
            {hasValue ? value : "—"}
          </Typography>
          {unit && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: theme.palette.text.secondary }}>
        {label}
      </Typography>
    </Box>
  );
};

export default GaugeDial;
